import React, { useEffect, useState } from "react";
import { ShieldCheck, ChevronLeft, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { OFFER, BUSINESS, BEFORE_AFTER, REVIEWS, COUNTDOWN_MINUTES, NO_DEPOSIT, GHL_CALENDAR_URL } from "../mock";
import { buildAcuityUrl } from "../lib/acuity";
import { StarRating } from "./common";
import { trackPixel } from "../lib/pixel";
import { InlineDeposit } from "./InlineDeposit";

const Countdown = ({ minutes }) => {
  const [left, setLeft] = useState(minutes * 60);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  const box = (v, label) => (
    <div className="flex flex-col items-center">
      <span className="min-w-[54px] rounded-xl bg-brand-ink px-3 py-2 text-center font-serif text-2xl font-semibold text-brand-ivory md:text-3xl">{v}</span>
      <span className="mt-1 font-sans text-[10px] uppercase tracking-widest text-brand-muted">{label}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-3">
      {box(mm, "Minutes")}
      <span className="font-serif text-2xl text-brand-ink">:</span>
      {box(ss, "Seconds")}
    </div>
  );
};

const TrustBlock = () => (
  <>
    <div className="mt-8 flex flex-col items-center gap-2 text-center">
      <div className="flex items-center gap-2">
        <span className="font-serif text-2xl font-semibold text-brand-ink">{BUSINESS.rating}</span>
        <StarRating />
        <span className="font-sans text-sm text-brand-muted">{BUSINESS.reviewCount} Google reviews</span>
      </div>
      <p className="flex items-center gap-1.5 font-sans text-sm text-brand-muted">
        <MapPin size={15} className="text-brand-gold" /> {BUSINESS.address}
      </p>
    </div>
    <div className="mt-10">
      <h3 className="text-center font-serif text-2xl text-brand-ink">Real clients, real results</h3>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {BEFORE_AFTER.map((item, i) => (
          <div key={i} className="overflow-hidden rounded-2xl bg-white shadow-[0_18px_50px_-28px_rgba(35,35,32,0.5)] ring-1 ring-brand-cream">
            <div className="relative">
              <img src={item.src} alt={`Before and after — ${item.name}`} className="w-full object-cover" />
              <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-between px-3 py-2">
                <span className="rounded-full bg-brand-ink/60 px-3 py-1 font-sans text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur">Before</span>
                <span className="rounded-full bg-brand-sage/80 px-3 py-1 font-sans text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur">After</span>
              </div>
            </div>
            <p className="px-4 py-3 font-serif text-lg text-brand-ink">{item.name}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-10">
      <h3 className="text-center font-serif text-2xl text-brand-ink">What our clients say</h3>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {REVIEWS.slice(0, 4).map((r, i) => (
          <div key={i} className="rounded-2xl bg-white p-5 shadow-[0_16px_45px_-28px_rgba(35,35,32,0.45)] ring-1 ring-brand-cream">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-sage/10 font-serif text-base font-semibold text-brand-sage">{r.initial}</div>
              <div>
                <p className="font-sans text-sm font-semibold text-brand-ink">{r.name}</p>
                <StarRating size={12} />
              </div>
            </div>
            <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">“{r.text}”</p>
          </div>
        ))}
      </div>
    </div>
  </>
);

const BookedConfirmation = ({ firstName }) => (
  <>
    <div className="rounded-3xl bg-white p-8 text-center shadow-[0_30px_80px_-35px_rgba(35,35,32,0.35)] ring-1 ring-brand-cream md:p-10">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-sage/10">
        <CheckCircle2 className="text-brand-sage" size={36} />
      </div>
      <h1 className="mt-5 font-serif text-3xl leading-tight text-brand-ink md:text-4xl">You're all set, {firstName}!</h1>
      <p className="mx-auto mt-3 max-w-md font-sans text-[15px] leading-relaxed text-brand-muted">
        Your appointment is confirmed — no deposit or payment needed. Look out for a confirmation by text and email with all the details. We can't wait to see you!
      </p>
    </div>
    <TrustBlock />
  </>
);

export const SchedulingView = ({ booking, onBack, initialStage = "schedule" }) => {
  const firstName = (booking?.name || "").trim().split(" ")[0] || "there";
  const email = (booking?.email || "").trim();
  const acuityUrl = buildAcuityUrl(booking || {});
  // Prefill the GHL booking calendar with details captured in the quiz
  const ghlBookingUrl = (() => {
    if (!GHL_CALENDAR_URL) return "";
    const parts = (booking?.name || "").trim().split(/\s+/).filter(Boolean);
    const p = new URLSearchParams();
    if (parts[0]) p.set("first_name", parts[0]);
    if (parts.length > 1) p.set("last_name", parts.slice(1).join(" "));
    if (booking?.email) p.set("email", booking.email);
    if (booking?.phone) p.set("phone", booking.phone);
    const qs = p.toString();
    return qs ? `${GHL_CALENDAR_URL}${GHL_CALENDAR_URL.includes("?") ? "&" : "?"}${qs}` : GHL_CALENDAR_URL;
  })();
  const slug = window.location.pathname.replace(/^\/+|\/+$/g, "").split("/")[0];
  const [stage, setStage] = useState(initialStage); // schedule | deposit

  const goNext = () => {
    setStage(NO_DEPOSIT ? "booked" : "deposit");
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPixel("Schedule", { content_name: OFFER.name });
    if (GHL_CALENDAR_URL) {
      // GoHighLevel embed auto-resizes its iframe via this helper script
      if (!document.getElementById("ghl-form-embed-js")) {
        const s = document.createElement("script");
        s.id = "ghl-form-embed-js";
        s.src = "https://link.msgsndr.com/js/form_embed.js";
        s.async = true;
        document.body.appendChild(s);
      }
    } else if (acuityUrl && !document.getElementById("acuity-embed-js")) {
      // Acuity embed.js auto-resizes the scheduler iframe so it reads as native page content
      const s = document.createElement("script");
      s.id = "acuity-embed-js";
      s.src = "https://embed.acuityscheduling.com/js/embed.js";
      s.async = true;
      document.body.appendChild(s);
    }
    // Fallback: some Acuity setups broadcast a custom confirmation message
    const onMsg = (e) => {
      const d = e?.data;
      const customSignal = d && (d.event === "acuity_booking_confirmed" || d === "acuity_booking_confirmed");
      if (customSignal) goNext();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Reliable auto-advance: poll our backend, which asks Acuity if a new booking
  // exists for this email. Acuity iframes don't emit a scheduled event for free
  // bookings, so this API check is the dependable signal.
  useEffect(() => {
    if (stage !== "schedule" || !acuityUrl || !email || GHL_CALENDAR_URL) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    let baseline = null;
    let baselineSet = false;
    let advanced = false;
    const check = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/acuity/check-booking?slug=${encodeURIComponent(slug)}&email=${encodeURIComponent(email)}`);
        const d = await r.json();
        if (!d || d.configured === false) return; // no API keys → keep manual button
        const id = d.appointment_id || null;
        if (!baselineSet) { baseline = id; baselineSet = true; return; }
        if (id && id !== baseline && !advanced) {
          advanced = true;
          try {
            const lead = JSON.parse(localStorage.getItem("db_active_lead") || "{}");
            localStorage.setItem("db_active_lead", JSON.stringify({ ...lead, acuity_appointment_id: id }));
          } catch (_) {}
          goNext();
        }
      } catch (_) {}
    };
    check();
    const t = setInterval(check, 4000);
    return () => clearInterval(t);
  }, [stage, email, slug, acuityUrl]);

  return (
    <div className="min-h-screen bg-brand-ivory">
      <div className="w-full bg-brand-sage py-2.5 text-center">
        <p className="font-sans text-[13px] font-medium tracking-wide text-brand-ivory">
          {NO_DEPOSIT ? `Your $${OFFER.price} voucher is reserved — pick your time below` : `Your $${OFFER.price} voucher is reserved — complete the steps below`}
        </p>
      </div>

      <div className="mx-auto max-w-3xl px-5 py-10 md:px-8">
        <button
          onClick={stage !== "schedule" ? () => setStage("schedule") : onBack}
          className="mb-6 flex items-center gap-1 font-sans text-sm text-brand-muted transition hover:text-brand-ink"
        >
          <ChevronLeft size={16} /> Back
        </button>

        {/* Step indicator */}
        {!NO_DEPOSIT && (
          <div className="mb-6 flex items-center justify-center gap-3 font-sans text-xs font-semibold uppercase tracking-widest">
            <span className={stage === "schedule" ? "text-brand-sage" : "text-brand-muted"}>1 · Pick your time</span>
            <span className="h-px w-8 bg-brand-cream" />
            <span className={stage === "deposit" ? "text-brand-sage" : "text-brand-muted"}>2 · Secure your spot</span>
          </div>
        )}

        {stage === "schedule" ? (
          <>
            <div className="rounded-3xl bg-white p-6 text-center shadow-[0_30px_80px_-35px_rgba(35,35,32,0.35)] ring-1 ring-brand-cream md:p-9">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-brand-golddark">Step 1 · You're almost done, {firstName}</p>
              <h1 className="mt-3 font-serif text-3xl leading-tight text-brand-ink md:text-4xl">Pick your appointment time</h1>
              <p className="mt-2 font-sans text-sm text-brand-muted">
                Choose a time below to reserve {OFFER.name} for just{" "}
                <span className="font-semibold text-brand-sage">${OFFER.price}</span>{" "}
                <span className="line-through">${OFFER.regPrice}</span>.
              </p>
              <div className="mt-6 flex flex-col items-center gap-2">
                <p className="font-sans text-xs font-semibold uppercase tracking-widest text-brand-muted">Your spot is saved for</p>
                <Countdown minutes={COUNTDOWN_MINUTES} />
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-2xl bg-white ring-1 ring-brand-cream">
              {GHL_CALENDAR_URL ? (
                <iframe
                  title="Book your appointment"
                  src={ghlBookingUrl}
                  id="ghl-booking-iframe"
                  allow="payment"
                  scrolling="no"
                  className="w-full"
                  style={{ minHeight: "700px", border: 0, overflow: "hidden", display: "block" }}
                />
              ) : acuityUrl ? (
                <iframe
                  title="Book your appointment"
                  src={acuityUrl}
                  className="w-full"
                  style={{ minHeight: "620px", border: 0, display: "block" }}
                  width="100%"
                  height="800"
                />
              ) : (
                <div className="py-10 text-center">
                  <h3 className="font-serif text-2xl text-brand-ink">We'll confirm your time, {firstName}!</h3>
                  <p className="mx-auto mt-2 max-w-sm font-sans text-sm text-brand-muted">
                    We've received your details and will reach out shortly to confirm your appointment.
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={goNext}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-sage px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-brand-ivory shadow-[0_10px_30px_-8px_rgba(46,69,57,0.6)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sagedark"
            >
              {NO_DEPOSIT ? (<>I've booked my time <ArrowRight size={16} /></>) : (<>I've picked my time — continue <ArrowRight size={16} /></>)}
            </button>
            <p className="mt-2 text-center font-sans text-xs text-brand-muted">
              {NO_DEPOSIT ? "No deposit needed — booking is completely free" : `Next: submit your refundable $${OFFER.deposit} deposit to lock it in`}
            </p>

            <TrustBlock />
          </>
        ) : stage === "booked" ? (
          <BookedConfirmation firstName={firstName} />
        ) : (
          <>
            <div className="rounded-3xl border border-brand-gold/40 bg-brand-cream/60 p-6 md:p-9">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 flex-shrink-0 text-brand-sage" size={24} />
                <div>
                  <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-brand-golddark">Step 2 · Last step</p>
                  <h2 className="mt-1 font-serif text-2xl text-brand-ink md:text-3xl">Wait {firstName} — you're not done yet</h2>
                  <p className="mt-3 font-sans text-[15px] leading-relaxed text-brand-ink">
                    The last step is to submit a <strong>refundable ${OFFER.deposit} deposit</strong> to secure your slot and get your bonus for FREE.
                  </p>
                  <p className="mt-2 font-sans text-sm italic text-brand-muted">
                    *If after the FREE consultation you decide not to get the treatment, we'll refund your deposit.
                  </p>
                  <p className="mt-2 font-sans text-sm text-brand-muted">
                    This deposit is applied as a credit toward this promotional treatment offer.
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl bg-white p-5 ring-1 ring-brand-cream md:p-6">
                <p className="mb-4 font-serif text-lg text-brand-ink">Submit your refundable ${OFFER.deposit} deposit</p>
                <InlineDeposit slug={slug} booking={booking} />
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <Countdown minutes={COUNTDOWN_MINUTES} />
            </div>

            <TrustBlock />
          </>
        )}

        <p className="mt-10 text-center font-sans text-xs text-brand-muted">*Limited to the first {BUSINESS.spotsLeft} people only · Individual results may vary</p>
      </div>
    </div>
  );
};
